import { CountryRequestData, TravelRequestData, TravelRequestHistoryRecord, TravelRequestPermissions } from 'api/travel-request-detail';
import { deepComputedFrom } from 'aurelia-deep-computed';
import { autoinject, BindingEngine, bindingMode, computedFrom, customElement, Disposable } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ValidationRules } from 'aurelia-validation';
import { TooltipCustomAttribute } from 'framework/html_behaviors/bs-tooltip';
import { log } from 'framework/script_utilities/dev-decorators';
import { Country } from 'model/business-keywords';
import { ChecklistItem, extractApprovableCountries, initContractorTraveler, initCorporateTraveler, initCountryExperience, produceCountryRequest, TravelRequest } from 'model/forms/travel-request';
import { extractTripCountries } from 'model/forms/trip-details';
import { SafetyService } from 'services/safety-service';
import { ModelBase } from 'support/base_classes/model-base';
import { RequestAttachmentsController } from './request-attachments-panel';
import { ApproveType } from 'api/swagger/ApiClient';

@autoinject
@customElement('travel-request-form')
export class TravelRequestFormCustomElement extends ModelBase<TravelRequest | TravelRequestData> {
  @bindable permissions: TravelRequestPermissions;
  @bindable history: TravelRequestHistoryRecord[];
  @bindable autofocus = false;
  @bindable disabled = false;
  @bindable({ defaultBindingMode: bindingMode.fromView }) isAnyApprovableCountry = false;
  @bindable attachmentsController: RequestAttachmentsController = null;
  protected corporateTravelerTooltip: TooltipCustomAttribute;
  protected contractorTravelerTooltip: TooltipCustomAttribute;
  private tripCountriesSubscription: Disposable = null;
  private approvableCountriesSubscription: Disposable = null;

  constructor(private safety: SafetyService, bindingEngine: BindingEngine) {
    super(bindingEngine);
  }

  attached() {
    this.tripCountriesSubscription = this.bindingEngine.propertyObserver(this, 'tripCountries').subscribe((newValue, oldValue) => this.tripCountriesChanged(newValue, oldValue));
    this.approvableCountriesSubscription = this.bindingEngine.propertyObserver(this, 'approvableCountries').subscribe((newValue, oldValue) => this.approvableCountriesChanged(newValue, oldValue));
  }

  detached() {
    this.tripCountriesSubscription.dispose();
    this.approvableCountriesSubscription.dispose();
  }

  modelChanged(newValue: TravelRequest | TravelRequestData) {
    super.modelChanged(newValue);
    this.isAnyApprovableCountry = this.approvableCountries.length > 0;
    ValidationRules
      .ensureObject<TravelRequest>().required()
      .ensure(model => model.name).displayName('Trip name').required()
      .ensure(model => model.travelers).displayName('Travellers section').minItems(1)
      .ensure(model => model.segments).displayName('Trip Itinerary section').minItems(1)
      .ensure(model => model.checklistItems).satisfies((items: ChecklistItem[]) => items.every(i => i.checked || !i.mandatory)).withMessage('Travel Checklist items need to be checked.')
      .on(this.model);
  }

  @deepComputedFrom('model.segments')
  get tripCountries() {
    return extractTripCountries(this.model.segments);
  }

  @deepComputedFrom('model.countryRequests')
  get approvableCountries() {
    return extractApprovableCountries(this.model.countryRequests);
  }

  private tripCountriesChanged(newValue: Country[], oldValue: Country[]) {
    if (this.changed && (newValue.length !== oldValue.length || newValue.some((v, i) => oldValue[i].id !== v.id))) {
      this.updateCountryRequests(newValue);
      this.updateChecklistItems(newValue.map(c => c.id));
    }
  }

  private approvableCountriesChanged(newValue: Country[], oldValue: Country[]) {
    this.isAnyApprovableCountry = newValue.length > 0;
    if (this.changed && (newValue.length !== oldValue.length || newValue.some((v, i) => oldValue[i].id !== v.id)))
      this.updateCountryExperiences(newValue);
  }

  update() {
    const tripCountries = this.tripCountries;
    this.updateCountryRequests(tripCountries);
    this.updateChecklistItems(tripCountries.map(c => c.id));
  }

  @log('debug')
  private updateCountryRequests(countries: Country[]) {
    this.model.countryRequests.splice(0, this.model.countryRequests.length, ...countries
      .map(c => ({ country: c, settings: this.safety.countrySettings.get(c.id), previous: this.model.countryRequests.find(r => r.country.id === c.id) })).filter(x => x.settings != null)
      .map(x => produceCountryRequest(x.country, x.settings, x.previous?.transit ?? false, x.previous?.approval?.approvalTypeValue === ApproveType.One && x.previous.approval.approvers.length === 1 ? x.previous.approval.approvers[0].approver : null)));
  }

  @log('debug')
  private updateChecklistItems(countryIds: number[]) {
    this.model.checklistItems.splice(0, this.model.checklistItems.length, ...this.safety.getApplicableQuestions(countryIds)
      .map(q => { return { message: q.message, checked: this.model.checklistItems.find(i => i.message === q.message)?.checked ?? q.defaultChecked, mandatory: q.mandatory }; }));
  }

  @log('debug')
  private updateCountryExperiences(countries: Country[]) {
    this.model.travelers.forEach(t => t.countryExperiences.splice(0, t.countryExperiences.length, ...countries
      .map(c => initCountryExperience({ country: c, revisit: t.countryExperiences.find(e => e.country.id === c.id)?.revisit ?? null }))));
  }

  @computedFrom('permissions')
  get approvingEnabled() {
    return this.permissions != null && this.permissions.canApprove;
  }

  @computedFrom('model', 'permissions')
  get countryApprovers() {
    return this.permissions == null ? [] : (this.model.countryRequests as CountryRequestData[])
      .filter(r => r.approval != null)
      .flatMap(r => r.approval.approvers.map(a => ({ country: r.country, approver: a })))
      .filter(x => this.permissions.approvableItems.some(i => i.countryId === x.country.id && i.approverId === x.approver.approver.id));
  }

  addCorporateTraveler() {
    const corporateTraveler = initCorporateTraveler({ countryExperiences: this.approvableCountries.map(c => initCountryExperience({ country: c })) });
    this.model.travelers.push(corporateTraveler);
    this.corporateTravelerTooltip.hide();
  }

  addContractorTraveler() {
    const contractorTraveler = initContractorTraveler({ countryExperiences: this.approvableCountries.map(c => initCountryExperience({ country: c })) });
    this.model.travelers.push(contractorTraveler);
    this.contractorTravelerTooltip.hide();
  }

  protected modelJsonReplacer(key: string, value) {
    if (['status', 'comment', 'modifiedBy', 'modifiedOn'].includes(key)) return undefined;
    if (key === 'approver' && value.id) return { id: value.id };
    return super.modelJsonReplacer(key, value);
  }
}
